import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {
  SearchResults,
  ProgressiveResults,
  SearchOptions,
  PositionOptions,
  FileInfo,
  SearchMode,
  MatchType,
  ContentSource,
  ContentInfo
} from '@/types'

interface SearchState {
  // Search input
  searchString: string
  setSearchString: (value: string) => void

  // Search options
  searchOptions: SearchOptions
  setSearchOptions: (options: Partial<SearchOptions>) => void
  setSearchMode: (mode: SearchMode) => void  // Exclusive - only one at a time
  toggleMatchType: (type: MatchType) => void  // Multi-select
  setNumberType: (type: 'digits' | 'written' | 'both') => void

  // Position mode options
  togglePositionSkip: (charIndex: number) => void
  togglePositionRepeat: (charIndex: number) => void
  togglePositionExact: (charIndex: number) => void
  togglePositionDerivate: () => void
  resetPositionOptions: () => void

  // Results
  searchResults: SearchResults | null
  setSearchResults: (results: SearchResults | null) => void

  // Progressive search
  progressiveResults: ProgressiveResults
  setProgressiveResults: (results: ProgressiveResults) => void
  updateProgressivePhase: (phase: MatchType, result: SearchResults) => void
  isProgressiveMode: boolean
  setIsProgressiveMode: (value: boolean) => void

  // Loading & error states
  isLoading: boolean
  setIsLoading: (value: boolean) => void
  error: string | null
  setError: (error: string | null) => void

  // Current file
  currentFileId: string | null
  setCurrentFileId: (id: string | null) => void
  currentFileInfo: FileInfo | null
  setCurrentFileInfo: (info: FileInfo | null) => void
  currentText: string
  setCurrentText: (text: string) => void

  // Content source tracking
  contentSource: ContentSource | null
  contentInfo: ContentInfo | null
  setContentSource: (source: ContentSource | null) => void
  setContentInfo: (info: ContentInfo | null) => void
  loadPastedText: (text: string) => void
  loadUrlContent: (url: string, content: string) => void

  // Text viewer
  showTextViewer: boolean
  setShowTextViewer: (value: boolean) => void

  // Actions
  clearSearch: () => void
  clearFile: () => void
  clearContent: () => void
  reset: () => void
}

const initialPositionOptions: PositionOptions = {
  skip: [],
  repeat: [],
  exact: [],
  derivate: false
}

const initialSearchOptions: SearchOptions = {
  searchMode: 'literal',  // Default: literal search
  matchTypes: ['exact'],  // Default: exact match
  progressive: false,
  numberType: 'both',
  positionOptions: initialPositionOptions
}

export const useSearchStore = create<SearchState>()(
  devtools(
    (set) => ({
      // Search input
      searchString: '',
      setSearchString: (value) =>
        set((state) => ({
          searchString: value,
          // Reset position options when search string changes
          searchOptions: {
            ...state.searchOptions,
            positionOptions: initialPositionOptions
          }
        })),

      // Search options
      searchOptions: initialSearchOptions,
      setSearchOptions: (options) =>
        set((state) => ({
          searchOptions: { ...state.searchOptions, ...options }
        })),
      setSearchMode: (mode) =>
        set((state) => {
          const isNumbersMode = mode === 'numbers'

          // Always reset match types when changing modes
          // Numbers mode: empty (sentences off by default, can be toggled)
          // Other modes: ['exact'] as default
          const newMatchTypes: MatchType[] = isNumbersMode ? [] : ['exact']

          return {
            searchOptions: {
              ...state.searchOptions,
              searchMode: mode,
              matchTypes: newMatchTypes,
              positionOptions: initialPositionOptions  // Reset position options on mode change
            }
          }
        }),
      toggleMatchType: (type) =>
        set((state) => {
          const currentTypes = state.searchOptions.matchTypes
          const hasType = currentTypes.includes(type)
          const isNumbersMode = state.searchOptions.searchMode === 'numbers'

          // In numbers mode, only sentences can be toggled and it can be empty
          if (isNumbersMode) {
            if (type !== 'sentences') return state
            return {
              searchOptions: {
                ...state.searchOptions,
                matchTypes: hasType ? [] : ['sentences']
              }
            }
          }

          // Don't allow removing the last match type (at least one must be selected)
          if (hasType && currentTypes.length === 1) {
            return state
          }

          return {
            searchOptions: {
              ...state.searchOptions,
              matchTypes: hasType
                ? currentTypes.filter((t) => t !== type)
                : [...currentTypes, type]
            }
          }
        }),
      setNumberType: (type) =>
        set((state) => ({
          searchOptions: { ...state.searchOptions, numberType: type }
        })),

      // Position mode options
      togglePositionSkip: (charIndex) =>
        set((state) => {
          const current = state.searchOptions.positionOptions?.skip || []
          const hasIndex = current.includes(charIndex)
          return {
            searchOptions: {
              ...state.searchOptions,
              positionOptions: {
                ...state.searchOptions.positionOptions || initialPositionOptions,
                skip: hasIndex
                  ? current.filter((i) => i !== charIndex)
                  : [...current, charIndex]
              }
            }
          }
        }),
      togglePositionRepeat: (charIndex) =>
        set((state) => {
          const current = state.searchOptions.positionOptions?.repeat || []
          const hasIndex = current.includes(charIndex)
          return {
            searchOptions: {
              ...state.searchOptions,
              positionOptions: {
                ...state.searchOptions.positionOptions || initialPositionOptions,
                repeat: hasIndex
                  ? current.filter((i) => i !== charIndex)
                  : [...current, charIndex]
              }
            }
          }
        }),
      togglePositionExact: (charIndex) =>
        set((state) => {
          const current = state.searchOptions.positionOptions?.exact || []
          const hasIndex = current.includes(charIndex)
          return {
            searchOptions: {
              ...state.searchOptions,
              positionOptions: {
                ...state.searchOptions.positionOptions || initialPositionOptions,
                exact: hasIndex
                  ? current.filter((i) => i !== charIndex)
                  : [...current, charIndex]
              }
            }
          }
        }),
      togglePositionDerivate: () =>
        set((state) => ({
          searchOptions: {
            ...state.searchOptions,
            positionOptions: {
              ...state.searchOptions.positionOptions || initialPositionOptions,
              derivate: !state.searchOptions.positionOptions?.derivate
            }
          }
        })),
      resetPositionOptions: () =>
        set((state) => ({
          searchOptions: {
            ...state.searchOptions,
            positionOptions: initialPositionOptions
          }
        })),

      // Results
      searchResults: null,
      setSearchResults: (results) => set({ searchResults: results }),

      // Progressive search
      progressiveResults: {},
      setProgressiveResults: (results) => set({ progressiveResults: results }),
      updateProgressivePhase: (phase, result) =>
        set((state) => ({
          progressiveResults: { ...state.progressiveResults, [phase]: result }
        })),
      isProgressiveMode: false,
      setIsProgressiveMode: (value) => set({ isProgressiveMode: value }),

      // Loading & error
      isLoading: false,
      setIsLoading: (value) => set({ isLoading: value }),
      error: null,
      setError: (error) => set({ error }),

      // Current file
      currentFileId: null,
      setCurrentFileId: (id) => set({ currentFileId: id }),
      currentFileInfo: null,
      setCurrentFileInfo: (info) => set({ currentFileInfo: info }),
      currentText: '',
      setCurrentText: (text) => set({ currentText: text }),

      // Content source tracking
      contentSource: null,
      contentInfo: null,
      setContentSource: (source) => set({ contentSource: source }),
      setContentInfo: (info) => set({ contentInfo: info }),
      loadPastedText: (text) =>
        set({
          currentText: text,
          contentSource: 'paste',
          contentInfo: {
            source: 'paste',
            name: 'Pasted Text',
            size: new Blob([text]).size
          },
          currentFileId: null,
          currentFileInfo: {
            fileId: 'pasted',
            filename: 'Pasted Text',
            contentLength: text.length,
            content: text
          },
          searchResults: null,
          error: null
        }),
      loadUrlContent: (url, content) =>
        set({
          currentText: content,
          contentSource: 'url',
          contentInfo: {
            source: 'url',
            name: url,
            size: new Blob([content]).size
          },
          currentFileId: null,
          currentFileInfo: {
            fileId: 'url',
            filename: new URL(url).hostname,
            contentLength: content.length,
            content: content
          },
          searchResults: null,
          error: null
        }),

      // Text viewer
      showTextViewer: false,
      setShowTextViewer: (value) => set({ showTextViewer: value }),

      // Actions
      clearSearch: () =>
        set({
          searchString: '',
          searchResults: null,
          progressiveResults: {},
          isProgressiveMode: false,
          error: null
        }),
      clearFile: () =>
        set({
          currentFileId: null,
          currentFileInfo: null,
          currentText: '',
          contentSource: null,
          contentInfo: null,
          searchResults: null,
          error: null
        }),
      clearContent: () =>
        set({
          currentFileId: null,
          currentFileInfo: null,
          currentText: '',
          contentSource: null,
          contentInfo: null,
          searchResults: null,
          error: null,
          showTextViewer: false
        }),
      reset: () =>
        set({
          searchString: '',
          searchOptions: initialSearchOptions,
          searchResults: null,
          progressiveResults: {},
          isProgressiveMode: false,
          isLoading: false,
          error: null,
          currentFileId: null,
          currentFileInfo: null,
          currentText: '',
          contentSource: null,
          contentInfo: null,
          showTextViewer: false
        })
    }),
    { name: 'search-store' }
  )
)
